import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useViewerIdentity from '../../hooks/useViewerIdentity';
import AvatarRing from '../common/AvatarRing';
import Button from '../common/ui/Button';
import ModerationBanner from '../common/ModerationBanner';
import VerifyEmailBanner from '../common/VerifyEmailBanner';
import '../../styles/layout.css';
import '../../styles/header-redesign.css';
import ThemeToggle from '../common/ThemeToggle';
import NotificationBell from '../NotificationBell/NotificationBell';
import SocialShareButtons from '../common/SocialShareButtons';
import { BRAND_NAME, DISCORD_INVITE_URL } from '../../constants/brand';
import { getGenres } from '../../services/genresApi';
import { FAQ_PAGES } from '../../data/faqPages';
import {
  HomeIcon,
  CompassIcon,
  BarChart2Icon,
  SearchIcon,
  PenToolIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  XIcon,
  ShieldIcon,
  MenuIcon,
  MessageCircleIcon,
  AlertTriangleIcon,
  CrownIcon,
  TagIcon,
  StarIcon,
  BookOpenIcon,
  FileTextIcon,
  GhostIcon,
  UsersIcon,
  ListOrderedIcon,
  TrendingUpIcon,
  LibraryIcon,
  ShieldXIcon,
  AwardIcon,
  Share2Icon,
  LayersIcon,
  DiscordIcon,
} from '../../assets/icons';

// Module-level cache: genres rarely change, so fetch once per app session.
// This prevents every PageLayout mount (i.e. every page navigation) from
// making a redundant GET /api/genres request.
let _genresCache = null;
let _genresPromise = null;

const getCachedGenres = () => {
  if (_genresCache) return Promise.resolve(_genresCache);
  if (_genresPromise) return _genresPromise;
  _genresPromise = getGenres()
    .then((genres) => {
      _genresCache = Array.isArray(genres)
        ? genres
            .filter((g) => g.name !== 'Ecchi')
            .sort((a, b) => (b.novelCount || 0) - (a.novelCount || 0))
            .slice(0, 6)
        : [];
      _genresPromise = null;
      return _genresCache;
    })
    .catch(() => {
      _genresPromise = null;
      return [];
    });
  return _genresPromise;
};

const primaryNavLinks = [
  { path: '/', label: 'Home', icon: HomeIcon },
  { path: '/library', label: 'Browse', icon: CompassIcon },
  { path: '/rankings', label: 'Rankings', icon: ListOrderedIcon },
  { path: '/characters/rankings', label: 'Characters', icon: GhostIcon },
  { path: '/discussions', label: 'Discussions', icon: MessageCircleIcon },
  { path: '/contests', label: 'Contests', icon: CrownIcon },
  { path: '/blog', label: 'Journal', icon: BookOpenIcon, external: true },
  { path: '/search', label: 'Search', icon: SearchIcon },
];


const UserHeaderAvatar = ({ user }) => {
  const [imgError, setImgError] = useState(false);
  
  useEffect(() => {
    setImgError(false);
  }, [user.profileImageUrl]);

  const displayName = user.fullName || user.username || 'User';
  const initials = displayName.slice(0, 2);
  const showImage = user.profileImageUrl && !imgError;

  return (
    <>
      {showImage ? (
        <img 
          src={user.profileImageUrl} 
          alt={displayName} 
          className="app-header__avatar-img"
          width="40"
          height="40"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="app-header__avatar-placeholder">
          {initials}
        </span>
      )}
    </>
  );
};

// headerProgress: initial width % of the thin progress bar under the header.
// headerProgressRef: optional ref handed to the fill element so callers (e.g.
// ChapterReader) can update width directly on scroll without React renders.
const PageLayout = ({ children, showSearch = true, noMobilePadding = false, allowStickyContent = false, headerBreadcrumb = null, headerExtraActions = null, headerProgress = null, headerProgressRef = null }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [footerGenres, setFooterGenres] = useState([]);
  const [drawerMounted, setDrawerMounted] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRafRef = useRef(null);
  const drawerTimeoutRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useViewerIdentity();

  useEffect(() => {
    let cancelled = false;
    getCachedGenres()
      .then((genres) => {
        if (!cancelled) {
          setFooterGenres(genres);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const closeOverlays = () => {
    setIsMobileNavOpen(false);
    setIsSearchExpanded(false);
  };

  const navItems = useMemo(() => {
    // BookShelf is shown to all users (guests see a landing page with a
    // register CTA; authenticated users see their own almanac).
    return [
      ...primaryNavLinks.slice(0, 6), // Home through Contests
      { path: '/year-in-review', label: 'BookShelf', icon: LibraryIcon },
      ...primaryNavLinks.slice(6), // Journal + Search
    ];
  }, []);
  const canAccessAuthorDashboard = Boolean(user?.authorId) || user?.roles?.includes('ROLE_SUPER_USER');


  useEffect(() => {
    closeOverlays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileNavOpen]);

  // Mount/unmount drawer to prevent off-screen fixed element from
  // extending html.scrollWidth (causes horizontal pan on mobile pinch-zoom).
  // The drawer is kept in the DOM only while open or transitioning.
  useEffect(() => {
    if (isMobileNavOpen) {
      if (drawerTimeoutRef.current) clearTimeout(drawerTimeoutRef.current);
      setDrawerMounted(true);
      drawerRafRef.current = requestAnimationFrame(() => {
        drawerRafRef.current = requestAnimationFrame(() => {
          setDrawerOpen(true);
        });
      });
    } else {
      if (drawerRafRef.current) cancelAnimationFrame(drawerRafRef.current);
      setDrawerOpen(false);
      drawerTimeoutRef.current = setTimeout(() => {
        setDrawerMounted(false);
      }, 350);
    }
    return () => {
      if (drawerRafRef.current) cancelAnimationFrame(drawerRafRef.current);
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    return () => {
      if (drawerTimeoutRef.current) clearTimeout(drawerTimeoutRef.current);
    };
  }, []);

  // Close drawer with Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMobileNavOpen) {
        closeOverlays();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileNavOpen]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      return;
    }

    closeOverlays();
    navigate(`/search?query=${encodeURIComponent(trimmed)}`);
    setSearchTerm('');
  };

  const handleSignOut = () => {
    closeOverlays();
    signOut();
    navigate('/', { replace: true });
  };

  const handleGoToProfile = () => {
    closeOverlays();
    if (!user) {
      navigate('/login');
      return;
    }
    navigate('/profile');
  };

  return (
    <div className={`layout ${allowStickyContent ? 'layout--allow-sticky-content' : ''}`}>
      <ModerationBanner />
      <VerifyEmailBanner />
      {/* Backdrop Overlay for Mobile Menu */}
      {isMobileNavOpen && (
        <div 
          className="app-header__backdrop"
          onClick={closeOverlays}
          aria-hidden="true"
        />
      )}

      {/* Mobile Side Drawer - conditionally mounted to prevent
          off-screen fixed element from extending html.scrollWidth */}
      {drawerMounted && (
      <div className={`app-header__drawer ${drawerOpen ? 'is-open' : ''}`}>
        <div className="app-header__drawer-header">
          <Link to="/" className="app-header__drawer-logo" onClick={closeOverlays}>
            <div className="app-header__logo-icon" aria-hidden="true">
              <img src="/logo192.png" alt="NovelDay" width="36" height="36" />
            </div>
            <span>{BRAND_NAME}</span>
          </Link>
          <button
            type="button"
            className="app-header__drawer-close"
            onClick={closeOverlays}
            aria-label="Close menu"
          >
            <XIcon width={24} height={24} />
          </button>
        </div>

        {/* Mobile Search in Drawer */}
        {showSearch && (
          <div className="app-header__drawer-search">
            <form onSubmit={handleSearchSubmit}>
              <input
                type="search"
                placeholder="Search novels..."
                aria-label="Search novels"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <Button type="submit" variant="primary" size="sm" className="btn--icon" aria-label="Submit search">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M8.25 14.25C11.5637 14.25 14.25 11.5637 14.25 8.25C14.25 4.93629 11.5637 2.25 8.25 2.25C4.93629 2.25 2.25 4.93629 2.25 8.25C2.25 11.5637 4.93629 14.25 8.25 14.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15.75 15.75L12.4875 12.4875" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Button>
            </form>
          </div>
        )}

        <nav className="app-header__drawer-nav">
          {navItems.map((link) => {
            const Icon = link.icon;
            const linkClass = `app-header__drawer-link ${isActive(link.path) ? 'is-active' : ''}`;
            if (link.external) {
              return (
                <a
                  key={link.path}
                  href={link.path}
                  className={linkClass}
                  onClick={closeOverlays}
                >
                  <span className="app-header__drawer-link-icon"><Icon width={20} height={20} /></span>
                  {link.label}
                </a>
              );
            }
            return (
              <Link
                key={link.path}
                to={link.path}
                className={linkClass}
                onClick={closeOverlays}
              >
                <span className="app-header__drawer-link-icon"><Icon width={20} height={20} /></span>
                {link.label}
              </Link>
            );
          })}
          <Link
            to="/write"
            className={`app-header__drawer-link ${isActive('/write') ? 'is-active' : ''}`}
            onClick={closeOverlays}
          >
            <span className="app-header__drawer-link-icon"><PenToolIcon width={20} height={20} /></span>
            Write
          </Link>
          <Link
            to="/progression"
            className={`app-header__drawer-link ${isActive('/progression') ? 'is-active' : ''}`}
            onClick={closeOverlays}
          >
            <span className="app-header__drawer-link-icon"><TrendingUpIcon width={20} height={20} /></span>
            Progression
          </Link>
          {canAccessAuthorDashboard && (
            <Link
              to="/author/dashboard"
              className={`app-header__drawer-link ${isActive('/author/dashboard') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><LayoutDashboardIcon width={20} height={20} /></span>
              Dashboard
            </Link>
          )}
          {user?.roles?.includes('ROLE_SUPER_USER') && (
            <Link
              to="/admin/takedowns"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/admin/takedowns') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><ShieldXIcon width={20} height={20} /></span>
              Takedowns
            </Link>
          )}
          {user?.roles?.includes('ROLE_SUPER_USER') && (
            <Link
              to="/admin/contests"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/admin/contests') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><CrownIcon width={20} height={20} /></span>
              Contests Admin
            </Link>
          )}
          {user?.roles?.includes('ROLE_SUPER_USER') && (
            <Link
              to="/admin/users"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/admin/users') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><UsersIcon width={20} height={20} /></span>
              User Management
            </Link>
          )}
          {user?.roles?.includes('ROLE_SUPER_USER') && (
            <Link
              to="/admin/reports"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/admin/reports') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><AlertTriangleIcon width={20} height={20} /></span>
              Issue Reports
            </Link>
          )}
          {user?.roles?.includes('ROLE_SUPER_USER') && (
            <Link
              to="/admin/badges"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/admin/badges') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><AwardIcon width={20} height={20} /></span>
              Badge Forge
            </Link>
          )}
          {user?.roles?.includes('ROLE_SUPER_USER') && (
            <Link
              to="/admin/referrals"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/admin/referrals') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><Share2Icon width={20} height={20} /></span>
              Referral Management
            </Link>
          )}
          {user?.roles?.includes('ROLE_SUPER_USER') && (
            <Link
              to="/admin/featured-authors"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/admin/featured-authors') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><StarIcon width={20} height={20} /></span>
              Featured Authors
            </Link>
          )}
          {user?.roles?.includes('ROLE_SUPER_USER') && (
            <Link
              to="/admin/tags"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/admin/tags') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><TagIcon width={20} height={20} /></span>
              Tag Management
            </Link>
          )}
          {user?.roles?.includes('ROLE_SUPER_USER') && (
            <Link
              to="/admin/genres"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/admin/genres') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><LayersIcon width={20} height={20} /></span>
              Genre Management
            </Link>
          )}
          {user?.roles?.includes('ROLE_SUPER_USER') && (
            <Link
              to="/admin/analytics"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/admin/analytics') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><BarChart2Icon width={20} height={20} /></span>
              Analytics
            </Link>
          )}
          {(user?.roles?.includes('ROLE_SUPER_USER') || user?.roles?.includes('ROLE_MODERATOR')) && (
            <Link
              to="/moderator/dashboard"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/moderator') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><ShieldIcon width={20} height={20} /></span>
              Mod Dashboard
            </Link>
          )}
          {user?.roles?.includes('ROLE_SUPER_USER') && (
            <Link
              to="/admin/blog"
              className={`app-header__drawer-link app-header__drawer-link--admin ${isActive('/admin/blog') ? 'is-active' : ''}`}
              onClick={closeOverlays}
            >
              <span className="app-header__drawer-link-icon"><FileTextIcon width={20} height={20} /></span>
              Blog Mod
            </Link>
          )}
        </nav>

        {/* Drawer Footer */}
        <div className="app-header__drawer-footer">
          {user ? (
            <>
              <button
                type="button"
                className="app-header__user-card"
                onClick={handleGoToProfile}
              >
                <AvatarRing
                  src={user.profileImageUrl}
                  alt={user.fullName || user.username}
                  tierId={user.tier || 'FIRST_PAGE'}
                  track={user.progressionDisplay || 'READER'}
                  size="sm"
                />
                <div className="app-header__user-details">
                  <span className="app-header__user-name">{user.fullName || user.username}</span>
                  <span className="app-header__user-role">
                    {user?.roles?.includes('ROLE_SUPER_USER') ? 'Admin' : user.authorId ? 'Author' : 'Reader'}
                  </span>
                </div>
              </button>
              <button
                type="button"
                className="app-header__logout-btn"
                onClick={handleSignOut}
              >
                <LogOutIcon width={18} height={18} />
                Logout
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="app-header__login-link"
              onClick={closeOverlays}
            >
              Login
            </Link>
          )}
        </div>
      </div>
      )}

      <header className={`app-header${headerBreadcrumb ? ' app-header--has-breadcrumb' : ''}`}>
        <div className="app-header__container">
          {/* Logo */}
          <div className="app-header__brand">
            <Link to="/" className="app-header__logo" aria-label={`${BRAND_NAME} - Home`}>
              <div className="app-header__logo-icon" aria-hidden="true">
                <img src="/logo192.png" alt="NovelDay" width="36" height="36" />
              </div>
              {!headerBreadcrumb && (
                <div className="app-header__logo-text">
                  <span className="app-header__logo-name">{BRAND_NAME}</span>
                </div>
              )}
            </Link>
            {headerBreadcrumb && (
              <div className="app-header__breadcrumb">
                {headerBreadcrumb}
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <nav className="app-header__nav" aria-label="Primary navigation">
            <ul className="app-header__nav-list">
              {navItems.map((link) => (
                <li key={link.path}>
                  {link.external ? (
                    <a
                      href={link.path}
                      className={`app-header__nav-link ${isActive(link.path) ? 'is-active' : ''}`}
                      onClick={closeOverlays}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      className={`app-header__nav-link ${isActive(link.path) ? 'is-active' : ''}`}
                      onClick={closeOverlays}
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
              <li>
                <Link
                  to="/write"
                  className={`app-header__nav-link ${isActive('/write') ? 'is-active' : ''}`}
                  onClick={closeOverlays}
                >
                  Write
                </Link>
              </li>
              <li>
                <Link
                  to="/progression"
                  className={`app-header__nav-link ${isActive('/progression') ? 'is-active' : ''}`}
                  onClick={closeOverlays}
                >
                  Progression
                </Link>
              </li>
              {canAccessAuthorDashboard && (
                <li>
                  <Link
                    to="/author/dashboard"
                    className={`app-header__nav-link ${isActive('/author/dashboard') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Dashboard
                  </Link>
                </li>
              )}
              {user?.roles?.includes('ROLE_SUPER_USER') && (
                <li>
                  <Link
                    to="/admin/takedowns"
                    className={`app-header__nav-link app-header__nav-link--admin ${isActive('/admin/takedowns') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Takedowns
                  </Link>
                </li>
              )}
              {user?.roles?.includes('ROLE_SUPER_USER') && (
                <li>
                  <Link
                    to="/admin/contests"
                    className={`app-header__nav-link app-header__nav-link--admin ${isActive('/admin/contests') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Contests
                  </Link>
                </li>
              )}
              {user?.roles?.includes('ROLE_SUPER_USER') && (
                <li>
                  <Link
                    to="/admin/users"
                    className={`app-header__nav-link app-header__nav-link--admin ${isActive('/admin/users') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Users
                  </Link>
                </li>
              )}
              {user?.roles?.includes('ROLE_SUPER_USER') && (
                <li>
                  <Link
                    to="/admin/reports"
                    className={`app-header__nav-link app-header__nav-link--admin ${isActive('/admin/reports') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Reports
                  </Link>
                </li>
              )}
              {user?.roles?.includes('ROLE_SUPER_USER') && (
                <li>
                  <Link
                    to="/admin/badges"
                    className={`app-header__nav-link app-header__nav-link--admin ${isActive('/admin/badges') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Badges
                  </Link>
                </li>
              )}
              {user?.roles?.includes('ROLE_SUPER_USER') && (
                <li>
                  <Link
                    to="/admin/referrals"
                    className={`app-header__nav-link app-header__nav-link--admin ${isActive('/admin/referrals') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Referrals
                  </Link>
                </li>
              )}
              {user?.roles?.includes('ROLE_SUPER_USER') && (
                <li>
                  <Link
                    to="/admin/featured-authors"
                    className={`app-header__nav-link app-header__nav-link--admin ${isActive('/admin/featured-authors') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Featured
                  </Link>
                </li>
              )}
              {user?.roles?.includes('ROLE_SUPER_USER') && (
                <li>
                  <Link
                    to="/admin/analytics"
                    className={`app-header__nav-link app-header__nav-link--admin ${isActive('/admin/analytics') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Analytics
                  </Link>
                </li>
              )}
              {user?.roles?.includes('ROLE_SUPER_USER') && (
                <li>
                  <Link
                    to="/admin/genres"
                    className={`app-header__nav-link app-header__nav-link--admin ${isActive('/admin/genres') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Genres
                  </Link>
                </li>
              )}
              {(user?.roles?.includes('ROLE_SUPER_USER') || user?.roles?.includes('ROLE_MODERATOR')) && (
                <li>
                  <Link
                    to="/moderator/dashboard"
                    className={`app-header__nav-link app-header__nav-link--admin ${isActive('/moderator') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Mod
                  </Link>
                </li>
              )}
              {user?.roles?.includes('ROLE_SUPER_USER') && (
                <li>
                  <Link
                    to="/admin/blog"
                    className={`app-header__nav-link app-header__nav-link--admin ${isActive('/admin/blog') ? 'is-active' : ''}`}
                    onClick={closeOverlays}
                  >
                    Blog Mod
                  </Link>
                </li>
              )}
            </ul>
          </nav>

          {/* Actions: Search, Theme, Auth */}
          <div className="app-header__actions">
            {headerExtraActions}
            {showSearch && (
              <div className={`app-header__search ${isSearchExpanded ? 'is-expanded' : ''}`}>
                <form onSubmit={handleSearchSubmit}>
                  <input
                    type="search"
                    placeholder="Search novels..."
                    aria-label="Search novels"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    onFocus={() => setIsSearchExpanded(true)}
                    onBlur={() => !searchTerm && setIsSearchExpanded(false)}
                  />
                  <Button type="submit" variant="primary" size="sm" className="btn--icon" aria-label="Submit search">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M8.25 14.25C11.5637 14.25 14.25 11.5637 14.25 8.25C14.25 4.93629 11.5637 2.25 8.25 2.25C4.93629 2.25 2.25 4.93629 2.25 8.25C2.25 11.5637 4.93629 14.25 8.25 14.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15.75 15.75L12.4875 12.4875" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Button>
                </form>
              </div>
            )}
            
            <ThemeToggle labelHidden />
            
            {user && <NotificationBell />}
            
            {user ? (
              <Button
                variant="ghost"
                className="app-header__avatar-btn"
                onClick={handleGoToProfile}
                aria-label="Go to profile"
                title={user.fullName || user.username}
              >
                <AvatarRing
                  src={user.profileImageUrl}
                  alt={user.fullName || user.username}
                  tierId={user.tier || 'FIRST_PAGE'}
                  track={user.progressionDisplay || 'READER'}
                  size="sm"
                />
              </Button>
            ) : (
              <Link 
                to="/login" 
                onClick={closeOverlays}
              >
                <Button variant="primary" size="sm">Login</Button>
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <button
              type="button"
              className={`app-header__mobile-toggle ${isMobileNavOpen ? 'is-active' : ''}`}
              aria-label={isMobileNavOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileNavOpen}
              onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
            >
              {isMobileNavOpen ? (
                <XIcon className="app-header__mobile-toggle-icon" />
              ) : (
                <MenuIcon className="app-header__mobile-toggle-icon" />
              )}
            </button>
          </div>
        </div>
        {headerProgress !== null && (
          <div className="app-header__progress-track" aria-hidden="true">
            <div className="app-header__progress-fill" ref={headerProgressRef} style={{ width: `${headerProgress}%` }} />
          </div>
        )}
      </header>

      <main className={`layout__main ${noMobilePadding ? 'layout__main--no-mobile-padding' : ''} ${allowStickyContent ? 'layout__main--allow-sticky-content' : ''}`}>
        {children}
      </main>

      <footer className="app-footer">
        <div className="container">
          <div className="app-footer__top">
            <div className="app-footer__brand-block">
              <Link to="/" className="app-footer__brand-link" aria-label={`${BRAND_NAME} — Home`}>
                <div className="app-footer__brand-mark" aria-hidden="true">
                  <img src="/logo192.png" alt="" width="36" height="36" />
                </div>
                <span className="app-footer__brand-name">{BRAND_NAME}</span>
              </Link>
              <p className="app-footer__tagline">
                Where every chapter is a new world. Discover, read, and share stories worth staying up for.
              </p>
              <div className="app-footer__social">
                <a href={DISCORD_INVITE_URL} className="app-footer__social-link" aria-label="Join our Discord" title="Join our Discord" target="_blank" rel="noopener noreferrer">
                  <DiscordIcon width={20} height={20} />
                </a>
                <Link to="/discussions" className="app-footer__social-link" aria-label="Discussions" title="Discussions">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                </Link>
                <Link to="/contests" className="app-footer__social-link" aria-label="Contests" title="Contests">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
                </Link>
                <a href="/blog" className="app-footer__social-link" aria-label="Journal" title="Journal">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                </a>
                <Link to="/year-in-review" className="app-footer__social-link" aria-label="BookShelf" title="BookShelf">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                </Link>
              </div>
              {/* Site-level share — unlike the social/community links above,
                  these open share dialogs for novelday.net itself (plus a
                  copy-link button). Any reader on any page can spread the
                  site from the footer. */}
              <div className="app-footer__share">
                <span className="app-footer__share-label">Share {BRAND_NAME}</span>
                <SocialShareButtons
                  url={typeof window !== 'undefined' ? window.location.origin : 'https://novelday.net'}
                  title={`${BRAND_NAME} — Discover, read & write web novels`}
                  copyLabel={`Copy ${BRAND_NAME} link`}
                />
              </div>
            </div>

            <div className="app-footer__links-grid">
              <div className="app-footer__col">
                <h4 className="app-footer__col-title">Discover</h4>
                <ul className="app-footer__col-list">
                  <li><Link to="/library">Browse Novels</Link></li>
                  <li><Link to="/search">Search</Link></li>
                  <li><Link to="/rankings">Rankings</Link></li>
                  <li><Link to="/characters/rankings">Characters</Link></li>
                  <li><Link to="/contests">Contests</Link></li>
                </ul>
              </div>
              <div className="app-footer__col">
                <h4 className="app-footer__col-title">Genres</h4>
                <ul className="app-footer__col-list">
                  {footerGenres.length > 0 ? (
                    footerGenres.map((genre) => (
                      <li key={genre.id}>
                        <Link to={genre.slug ? `/genre/${genre.slug}` : `/search?includeGenreIds=${genre.id}`}>{genre.name}</Link>
                      </li>
                    ))
                  ) : (
                    <li><Link to="/library">Browse all genres</Link></li>
                  )}
                </ul>
              </div>
              <div className="app-footer__col">
                <h4 className="app-footer__col-title">Community</h4>
                <ul className="app-footer__col-list">
                  <li><a href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer">Discord</a></li>
                  <li><Link to="/discussions">Discussions</Link></li>
                  <li><Link to="/year-in-review">BookShelf</Link></li>
                  <li><a href="/blog">Journal</a></li>
                  <li><Link to="/write">Write</Link></li>
                  <li><Link to="/progression">Progression</Link></li>
                </ul>
              </div>
              <div className="app-footer__col">
                <h4 className="app-footer__col-title">Guides</h4>
                <ul className="app-footer__col-list">
                  {FAQ_PAGES.map((page) => (
                    <li key={page.slug}>
                      <Link to={`/faq/${page.slug}`}>{page.navLabel || page.h1}</Link>
                    </li>
                  ))}
                  <li><Link to="/faq">All guides &amp; FAQ</Link></li>
                </ul>
              </div>
              <div className="app-footer__col">
                <h4 className="app-footer__col-title">Support</h4>
                <ul className="app-footer__col-list">
                  <li><Link to="/support#contact">Contact Us</Link></li>
                  <li><Link to="/support#faq">Help &amp; FAQ</Link></li>
                  <li><Link to="/legal/terms">Terms of Service</Link></li>
                  <li><Link to="/legal/privacy">Privacy Policy</Link></li>
                  <li><Link to="/legal/dmca">DMCA & Copyright</Link></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="app-footer__bottom">
            <p className="app-footer__copyright">
              &copy; {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
            </p>
            <p className="app-footer__made-with">
              Made with <span className="app-footer__heart" aria-hidden="true">&hearts;</span> for readers and writers.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageLayout;
